import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import {
  getInvitationCategory,
  getTemplatesByCategory,
  getVisibleInvitationCategories,
  invitationCategories,
  invitationTemplates,
} from '../lib/invitationCatalog.js';
import '../styles/Invitations.css';

const Invitations = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [filter, setFilter] = useState(categoryParam || 'all');

  useEffect(() => {
    setFilter(getInvitationCategory(categoryParam)?.value || 'all');
  }, [categoryParam]);

  const filteredTemplates = filter === 'all' ? invitationTemplates : getTemplatesByCategory(filter);
  const categoryLinks = getVisibleInvitationCategories();

  return (
    <div className="invitations">
      <div className="invitations-header">
        <span aria-hidden="true" className="invitations-hero-script">crafted for every celebration</span>
        <p className="invitations-eyebrow">Digital invitation templates in the Philippines</p>
        <h1>Digital Invitation Templates for Every Celebration</h1>
        <p>
          Browse mobile-friendly digital invitations for weddings, birthdays, christenings,
          corporate events, and anniversaries. Every template is designed for quick sharing,
          polished presentation, and RSVP-ready delivery.
        </p>
        <div className="pricing-badges">
          <p className="price-note">Templates: <strong>P200 - P300</strong></p>
          <p className="price-note custom">Custom Design: <strong>P350 - P700</strong></p>
        </div>
      </div>

      <section className="invitations-intro">
        <div className="invitations-copy-block">
          <h2>Find the right digital invitation faster</h2>
          <p>
            Use this page as your main template gallery, then visit the dedicated category pages
            below if you want a more focused set of wedding, birthday, christening, corporate,
            or anniversary invitation ideas.
          </p>
        </div>
        <div className="invitations-value-list">
          <article className="invitations-value-card">
            <span aria-hidden="true" className="value-card-icon">📲</span>
            <h3>Easy to share</h3>
            <p>Send your digital invitation through Messenger, Facebook, email, or group chats.</p>
          </article>
          <article className="invitations-value-card">
            <span aria-hidden="true" className="value-card-icon">📱</span>
            <h3>Made for mobile guests</h3>
            <p>Every invitation is built to look clean and readable on phones and tablets.</p>
          </article>
          <article className="invitations-value-card">
            <span aria-hidden="true" className="value-card-icon">✅</span>
            <h3>Ready for RSVP flow</h3>
            <p>Choose a template or upgrade to custom design without rebuilding your booking process.</p>
          </article>
        </div>
      </section>

      <section className="invitation-category-links-section">
        <div className="invitation-section-heading">
          <h2>Explore digital invitation categories</h2>
          <p>
            These landing pages are designed to help you compare templates by event type before you book.
          </p>
        </div>
        <div className="invitation-category-links">
          {categoryLinks.map((category) => (
            <Link key={category.value} to={category.path} className="invitation-category-link-card" data-category={category.value}>
              {category.icon && <span aria-hidden="true" className="invitation-category-icon">{category.icon}</span>}
              <span className="invitation-category-link-label">{category.label}</span>
              <h3>{category.headline}</h3>
              <p>{category.blurb}</p>
              <span aria-hidden="true" className="invitation-category-arrow">→</span>
            </Link>
          ))}
        </div>
      </section>

      <div className="templates-section-label">
        <h2>Browse Templates</h2>
        <span className="templates-count">{filteredTemplates.length} design{filteredTemplates.length !== 1 ? 's' : ''} available</span>
      </div>
      <div className="filter-section">
        <div className="filter-buttons">
          {invitationCategories.map((cat) => (
            <button
              key={cat.value}
              className={`filter-btn ${filter === cat.value ? 'active' : ''}`}
              onClick={() => setFilter(cat.value)}
            >
              {cat.icon && <span aria-hidden="true" className="filter-btn-icon">{cat.icon}</span>}
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="templates-grid">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="template-card-wrapper">
            {template.slug && (
              <Link to={`/${template.category}/templates/${template.slug}`} className="preview-overlay">
                <span className="preview-text">Click to Preview</span>
              </Link>
            )}
            <Card
              title={template.title}
              description={template.description}
              image={template.image}
              className="template-card"
              category={template.category}
              categoryLabel={getInvitationCategory(template.category)?.label || template.category}
            />
          </div>
        ))}
      </div>

      <section className="invitations-faq-teaser">
        <div className="invitation-section-heading">
          <h2>Why people choose digital invitations</h2>
          <p>
            Digital invitations are easier to update, faster to distribute, and more practical for RSVP tracking than static image files.
          </p>
        </div>
        <div className="invitations-value-list">
          <article className="invitations-value-card">
            <span aria-hidden="true" className="value-card-icon">⚡</span>
            <h3>Faster guest communication</h3>
            <p>Share one link with details, RSVP flow, and event updates instead of sending multiple files.</p>
          </article>
          <article className="invitations-value-card">
            <span aria-hidden="true" className="value-card-icon">🌐</span>
            <h3>Better for modern events</h3>
            <p>Use a link that guests can open instantly on mobile without downloading large attachments.</p>
          </article>
          <article className="invitations-value-card">
            <span aria-hidden="true" className="value-card-icon">✏️</span>
            <h3>Flexible for templates or custom work</h3>
            <p>Start with a ready-made design or move to a custom invitation when you need a unique theme.</p>
          </article>
        </div>
        <div className="invitations-faq-link-row">
          <Link to="/digital-invitation-faq" className="invitations-text-link">
            Read the digital invitation FAQ
          </Link>
        </div>
      </section>

      <div className="templates-cta">
        <h3>Don't see what you're looking for?</h3>
        <p>We can create custom designs based on your preferences and theme!</p>
        <p className="custom-price-note">Custom design price: <strong>P350 - P700</strong></p>
        <Link to="/book?type=custom">
          <Button variant="primary" size="large">
            Request Custom Design
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default Invitations;
